import { useState, useEffect } from 'react';
import { apiFetch, apiPost, apiPut, apiDelete } from '../api/client';
import { PageContainer } from '../components/PageContainer';
import type { ImageSource, ImageSourceInput } from '@posternauts/shared';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const LICENSE_OPTIONS = [
  { value: 'PUBLIC_DOMAIN',       label: 'Public Domain' },
  { value: 'CC_BY_3',             label: 'CC BY 3.0' },
  { value: 'CC_BY_4',             label: 'CC BY 4.0' },
  { value: 'PERMISSION_REQUIRED', label: 'Permission Required' },
];

function licenseBadge(type: string): { label: string; color: string } {
  switch (type) {
    case 'PUBLIC_DOMAIN':       return { label: 'Public Domain', color: '#1a4a2a' };
    case 'CC_BY_3':             return { label: 'CC BY 3.0',     color: '#1a3a6a' };
    case 'CC_BY_4':             return { label: 'CC BY 4.0',     color: '#1a3a6a' };
    case 'PERMISSION_REQUIRED': return { label: 'Permission Req.', color: '#6a2a1a' };
    default:                    return { label: type,             color: '#333' };
  }
}

const EMPTY_FORM: ImageSourceInput = {
  name:                '',
  slug:                '',
  archiveUrl:          '',
  apiBaseUrl:          null,
  licenseType:         'PUBLIC_DOMAIN',
  licenseUrl:          null,
  attributionRequired: false,
  attributionTemplate: null,
  notes:               null,
  isActive:            true,
  requiresApiKey:      false,
  apiKeyEnvVar:        null,
};

function sourceToInput(s: ImageSource): ImageSourceInput {
  return {
    name:                s.name,
    slug:                s.slug,
    archiveUrl:          s.archiveUrl,
    apiBaseUrl:          s.apiBaseUrl,
    licenseType:         s.licenseType,
    licenseUrl:          s.licenseUrl,
    attributionRequired: s.attributionRequired,
    attributionTemplate: s.attributionTemplate,
    notes:               s.notes,
    isActive:            s.isActive,
    requiresApiKey:      s.requiresApiKey,
    apiKeyEnvVar:        s.apiKeyEnvVar,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ImageSourcesPage() {
  const [sources, setSources]           = useState<ImageSource[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);

  // Modal state
  const [modalMode, setModalMode]       = useState<'add' | 'edit' | null>(null);
  const [editingId, setEditingId]       = useState<number | null>(null);
  const [form, setForm]                 = useState<ImageSourceInput>(EMPTY_FORM);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState<string | null>(null);

  // Delete confirmation
  const [deletingId, setDeletingId]     = useState<number | null>(null);
  const [deleteError, setDeleteError]   = useState<string | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = () => {
    setLoading(true);
    apiFetch<ImageSource[]>('/image-sources')
      .then(data => { setSources(data); setLoading(false); })
      .catch(err  => { setError(err.message); setLoading(false); });
  };

  useEffect(load, []);

  // ── Active toggle ─────────────────────────────────────────────────────────
  const toggleActive = async (source: ImageSource) => {
    try {
      const updated = await apiPut<ImageSource>(`/image-sources/${source.id}`, {
        isActive: !source.isActive,
      });
      setSources(prev => prev.map(s => s.id === updated.id ? updated : s));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  // ── Modal open/close ──────────────────────────────────────────────────────
  const openAdd = () => {
    setForm(EMPTY_FORM);
    setSaveError(null);
    setEditingId(null);
    setModalMode('add');
  };

  const openEdit = (source: ImageSource) => {
    setForm(sourceToInput(source));
    setSaveError(null);
    setEditingId(source.id);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
    setSaveError(null);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      if (modalMode === 'add') {
        const created = await apiPost<ImageSource>('/image-sources', form);
        setSources(prev => [...prev, created]);
      } else if (modalMode === 'edit' && editingId !== null) {
        const updated = await apiPut<ImageSource>(`/image-sources/${editingId}`, form);
        setSources(prev => prev.map(s => s.id === updated.id ? updated : s));
      }
      closeModal();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id: number) => {
    setDeleteError(null);
    try {
      await apiDelete(`/image-sources/${id}`);
      setSources(prev => prev.filter(s => s.id !== id));
      setDeletingId(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // ── Form helpers ──────────────────────────────────────────────────────────
  const setField = <K extends keyof ImageSourceInput>(key: K, value: ImageSourceInput[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const nullableStr = (v: string) => v.trim() === '' ? null : v.trim();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageContainer>
      <div style={{ padding: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0 }}>Image Sources</h2>
            <p style={docStyle}>
              Astronomical image archives available for discovery. Only active sources
              appear in the Discovery source selector. Sources with unsupported licenses
              (e.g. PERMISSION_REQUIRED) should remain inactive.
            </p>
          </div>
          <button onClick={openAdd} style={primaryBtn}>
            + Add Source
          </button>
        </div>

        {error && <div style={errorBox}>{error}</div>}

        {loading ? (
          <p style={{ color: '#888', marginTop: '2rem' }}>Loading…</p>
        ) : (
          <table style={tableStyle}>
            <thead>
            <tr>
              {['Name', 'Slug', 'License', 'Attribution', 'API Key Env', 'Active', ''].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
            </thead>
            <tbody>
            {sources.map(source => {
              const badge = licenseBadge(source.licenseType);
              return (
                <tr key={source.id} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: '#e0e0ff' }}>{source.name}</div>
                    {source.notes && (
                      <div style={{ fontSize: 11, color: '#666', marginTop: 2, maxWidth: 260 }} title={source.notes}>
                        {source.notes.length > 80 ? source.notes.slice(0, 80) + '…' : source.notes}
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <code style={codeStyle}>{source.slug}</code>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, background: badge.color }}>{badge.label}</span>
                    {source.attributionRequired && (
                      <span style={{ ...badgeStyle, background: '#4a3a00', marginLeft: 4 }}>Attribution</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: '#888', maxWidth: 180 }}>
                    {source.attributionTemplate ?? '—'}
                  </td>
                  <td style={tdStyle}>
                    {source.apiKeyEnvVar
                      ? <code style={codeStyle}>{source.apiKeyEnvVar}</code>
                      : <span style={{ color: '#555' }}>—</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={() => toggleActive(source)}
                      style={{
                        ...toggleBtn,
                        background: source.isActive ? '#1a4a2a' : '#2a2a3a',
                        color: source.isActive ? '#4ade80' : '#555',
                      }}
                      title={source.isActive ? 'Click to deactivate' : 'Click to activate'}
                    >
                      {source.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <button onClick={() => openEdit(source)} style={ghostBtn}>
                      Edit
                    </button>
                    <button
                      onClick={() => { setDeletingId(source.id); setDeleteError(null); }}
                      style={{ ...ghostBtn, color: '#f87171', marginLeft: 4 }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Add / Edit Modal ─────────────────────────────────────────────── */}
      {modalMode && (
        <Overlay onClick={closeModal}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1.25rem' }}>
              {modalMode === 'add' ? 'Add Image Source' : 'Edit Image Source'}
            </h3>

            <div style={formGrid}>
              <FormField label="Name *">
                <input
                  style={inputStyle}
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  placeholder="e.g. ESO"
                />
              </FormField>

              <FormField label="Slug *" hint="URL-safe, lowercase. Must be unique.">
                <input
                  style={inputStyle}
                  value={form.slug}
                  onChange={e => setField('slug', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  placeholder="e.g. eso"
                  disabled={modalMode === 'edit'} // slug is immutable after creation
                />
              </FormField>

              <FormField label="Archive URL *" fullWidth>
                <input
                  style={inputStyle}
                  value={form.archiveUrl}
                  onChange={e => setField('archiveUrl', e.target.value)}
                  placeholder="https://eso.org/public/images"
                />
              </FormField>

              <FormField label="API Base URL" fullWidth hint="Base URL for programmatic access, if applicable.">
                <input
                  style={inputStyle}
                  value={form.apiBaseUrl ?? ''}
                  onChange={e => setField('apiBaseUrl', nullableStr(e.target.value))}
                  placeholder="https://archive.eso.org/…"
                />
              </FormField>

              <FormField label="License Type *">
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={form.licenseType}
                  onChange={e => setField('licenseType', e.target.value)}
                >
                  {LICENSE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </FormField>

              <FormField label="License URL">
                <input
                  style={inputStyle}
                  value={form.licenseUrl ?? ''}
                  onChange={e => setField('licenseUrl', nullableStr(e.target.value))}
                  placeholder="https://creativecommons.org/…"
                />
              </FormField>

              <FormField label="Attribution Template" fullWidth hint='Template for credit line, e.g. "Credit: ESO/{{author}}"'>
                <input
                  style={inputStyle}
                  value={form.attributionTemplate ?? ''}
                  onChange={e => setField('attributionTemplate', nullableStr(e.target.value))}
                  placeholder="Credit: ESO"
                />
              </FormField>

              <FormField label="API Key Env Var" hint="Env var name, e.g. NASA_API_KEY">
                <input
                  style={inputStyle}
                  value={form.apiKeyEnvVar ?? ''}
                  onChange={e => setField('apiKeyEnvVar', nullableStr(e.target.value))}
                  placeholder="NASA_API_KEY"
                />
              </FormField>

              <FormField label="Notes" fullWidth>
                <textarea
                  style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }}
                  value={form.notes ?? ''}
                  onChange={e => setField('notes', nullableStr(e.target.value))}
                  placeholder="Any important caveats, restrictions, or context…"
                />
              </FormField>

              <div style={{ display: 'flex', gap: '1.5rem', gridColumn: '1 / -1' }}>
                <CheckField
                  label="Attribution Required"
                  checked={form.attributionRequired}
                  onChange={v => setField('attributionRequired', v)}
                />
                <CheckField
                  label="Requires API Key"
                  checked={form.requiresApiKey}
                  onChange={v => setField('requiresApiKey', v)}
                />
                <CheckField
                  label="Active"
                  checked={form.isActive}
                  onChange={v => setField('isActive', v)}
                />
              </div>
            </div>

            {saveError && <div style={{ ...errorBox, marginTop: '0.75rem' }}>{saveError}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
              <button onClick={closeModal} style={ghostBtn} disabled={saving}>Cancel</button>
              <button onClick={handleSave} style={primaryBtn} disabled={saving}>
                {saving ? 'Saving…' : modalMode === 'add' ? 'Add Source' : 'Save Changes'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {deletingId !== null && (
        <Overlay onClick={() => { setDeletingId(null); setDeleteError(null); }}>
          <div style={{ ...modalStyle, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.75rem' }}>Delete Image Source?</h3>
            <p style={{ color: '#aaa', fontSize: 14, margin: '0 0 0.5rem' }}>
              This source will be permanently removed. This cannot be undone.
            </p>
            <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>
              Sources with linked candidates cannot be deleted.
            </p>
            {deleteError && <div style={{ ...errorBox, marginTop: '0.75rem' }}>{deleteError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.25rem' }}>
              <button onClick={() => { setDeletingId(null); setDeleteError(null); }} style={ghostBtn}>
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deletingId)}
                style={{ ...primaryBtn, background: '#7f1d1d', borderColor: '#7f1d1d' }}
              >
                Delete
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </PageContainer>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Overlay({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

function FormField({
                     label, hint, children, fullWidth,
                   }: {
  label: string; hint?: string; children: React.ReactNode; fullWidth?: boolean;
}) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#aaa', marginBottom: 4 }}>
        {label}
        {hint && <span style={{ fontWeight: 400, color: '#666', marginLeft: 6 }}>{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function CheckField({
                      label, checked, onChange,
                    }: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: '#ccc' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const docStyle: React.CSSProperties = {
  fontSize: 13, color: 'rgb(160, 168, 255)', lineHeight: 1.55, margin: '0.4rem 0 0',
};

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginTop: '1.5rem', fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #222230',
  color: '#555', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
};

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid #1a1a28',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px', verticalAlign: 'middle',
};

const codeStyle: React.CSSProperties = {
  background: '#1a1a28', border: '1px solid #2a2a3a', borderRadius: 4,
  padding: '2px 6px', fontSize: 12, color: '#a0a8ff', fontFamily: 'monospace',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-block', padding: '2px 8px', borderRadius: 10,
  fontSize: 11, color: '#fff', fontWeight: 500,
};

const toggleBtn: React.CSSProperties = {
  border: '1px solid #2a2a3a', borderRadius: 4, padding: '3px 10px',
  fontSize: 12, fontWeight: 600, cursor: 'pointer',
};

const ghostBtn: React.CSSProperties = {
  background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 6,
  padding: '5px 12px', fontSize: 13, color: '#aaa', cursor: 'pointer',
};

const primaryBtn: React.CSSProperties = {
  background: '#2a2a6a', border: '1px solid #3a3a8a', borderRadius: 6,
  padding: '6px 16px', fontSize: 13, color: '#e0e0ff', cursor: 'pointer', fontWeight: 600,
};

const errorBox: React.CSSProperties = {
  background: '#3a1010', border: '1px solid #6a2020', borderRadius: 6,
  padding: '0.5rem 0.75rem', color: '#fca5a5', fontSize: 13,
};

const modalStyle: React.CSSProperties = {
  background: '#111118', border: '1px solid #222230', borderRadius: 10,
  padding: '1.75rem', width: '100%', maxWidth: 640, maxHeight: '90vh',
  overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
};

const formGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', background: '#0d0d18',
  border: '1px solid #2a2a3a', borderRadius: 6, color: '#e0e0ff',
  fontSize: 13, boxSizing: 'border-box',
};